'use client'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { Notification } from '../Notification'
import { getKindStyle } from '../kind-style'
import type { NotificationRenderer, NotificationViewProps } from './types'

function PreviewCard({
  notification,
  href,
  showReadAction = true,
  showDeleteAction = true,
  onMarkedRead,
  onDeleted,
}: NotificationViewProps) {
  const style = getKindStyle(notification.kind)

  const card = (
    <div
      className={cn(
        'w-full min-w-0 overflow-hidden rounded-lg border p-3 transition-all duration-150',
        'hover:shadow-md',
        style.borderClass,
        style.bgClass,
        href && 'cursor-pointer hover:bg-accent/40',
      )}
    >
      <Notification notification={notification}>
        <div className="flex items-start gap-3">
          <Notification.KindIcon className="mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <Notification.Title className="min-w-0 flex-1" />
              <Notification.KindBadge />
            </div>
            <Notification.Body preview className="mt-1" />
            <div className="mt-2 flex items-center justify-between gap-2">
              <Notification.Time className="text-[11px]" />
              {(showReadAction || showDeleteAction) && (
                <div className="flex gap-1">
                  {showReadAction && <Notification.ReadAction onSuccess={onMarkedRead} />}
                  {showDeleteAction && <Notification.DeleteAction onSuccess={onDeleted} />}
                </div>
              )}
            </div>
          </div>
        </div>
      </Notification>
    </div>
  )

  return href ? (
    <Link href={href} className="block">
      {card}
    </Link>
  ) : (
    card
  )
}

function FullView({
  notification,
  showReadAction = true,
  showDeleteAction = true,
  onMarkedRead,
  onDeleted,
}: NotificationViewProps) {
  return (
    <Notification notification={notification}>
      <article className="space-y-6">
        <header className="space-y-2">
          <div className="flex items-center gap-2">
            <Notification.KindIcon className="size-5" />
            <Notification.KindBadge />
          </div>
          <Notification.Title as="h1" className="text-2xl font-bold tracking-tight md:text-3xl" />
          <Notification.Time absolute className="block text-sm text-muted-foreground" />
        </header>

        <Notification.Body className="text-base leading-relaxed" />

        <Notification.Meta />

        {(showReadAction || showDeleteAction) && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-border pt-4">
            {showDeleteAction && <Notification.DeleteAction variant="labeled" onSuccess={onDeleted} />}
            {showReadAction && <Notification.ReadAction variant="labeled" onSuccess={onMarkedRead} />}
          </div>
        )}
      </article>
    </Notification>
  )
}

/** Fallback pra qualquer `type_id` sem renderer próprio. */
export const defaultRenderer: NotificationRenderer = {
  Preview: PreviewCard,
  Full: FullView,
}
